import React, { useEffect, useRef } from 'react';

// library components
import { Checkbox, MessageCard, NumberInput, Text, TextInput, Theme, i18n, tokens } from "../../index";

// Material-UI components
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";

/**
 * formType = default means it's either the combined bank account or the default reimbursement account. If formType is paymentsOnly then it's the bank account for only the premium payments
 * @param formType
 * @param state
 * @param setState
 * @param dispatch
 * @param isMemberPay Determines whether to display "deposit only" wording in legal verbiage. Using same bank account needs different verbiage based on non member pay/member pay divisions.
 * @returns {JSX.Element}
 * @private
 */
const EftDetailsForm = ({
  formType,
  eftDetails,
  handleStateChanges,
  handleChange,
  accountLevelBankingDetailsView = false,
  isMemberPay = false
}) => {
  const {
    accountHolder,
    accountHolderError,
    institutionAddress,
    institutionName,
    institutionNum,
    institutionNumError,
    transitNum,
    transitNumError,
    accountNum,
    accountNumError,
    accepted,
    acceptedError,
    isValidated,
    branchValidationError
  } = eftDetails[formType];
  const styles = {
    column: {
      display: 'flex',
      flexDirection: 'column'
    }
  };

  // reset validation state if any user inputs change as form will need to be checked again
  useEffect(() => {
    handleStateChanges(formType, {
      branchValidationError: false,
      isValidated: false
    });
  }, [institutionNum, transitNum, accountHolder, accountNum, accepted]);

  // Create a ref for each MessageCard
  const validatedMessageCardRef = useRef(null);
  const branchErrorCardRef = useRef(null);
  const acceptedErrorRef = useRef(null);

  // Scroll to the validated MessageCard when it is rendered
  useEffect(() => {
    if (isValidated && validatedMessageCardRef.current) {
      validatedMessageCardRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
    if (!isValidated && branchValidationError && branchErrorCardRef.current) {
      branchErrorCardRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
    if (acceptedError) {
      acceptedErrorRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [isValidated, branchValidationError, acceptedError]);

  /**
   * 
   * @returns 
   */
  const getHeadingText = () => {
    if (eftDetails.useSameBankAccount) {
      return /*#__PURE__*/React.createElement(Text, {
        large: true,
        bold: true,
        margin: "30px 0 0 0"
      }, i18n.t(tokens.titles.bankingDetails));
    } else if (formType === 'default') {
      return /*#__PURE__*/React.createElement(Text, {
        large: true,
        bold: true,
        margin: "30px 0 0 0"
      }, i18n.t(tokens.titles.reimbursementAccountDetails));
    }
    return /*#__PURE__*/React.createElement(Text, {
      large: true,
      bold: true,
      margin: "40px 0 0 0"
    }, i18n.t(tokens.titles.premiumPaymentAccountDetails));
  };

  /**
   * Retrieves the appropriate legal access verbiage.
   * @returns {string}
   */
  const _getLegalAccessVerbiage = () => {
    if (accountLevelBankingDetailsView) {
      return i18n.t(tokens.labels.banking.legalAccessVerbiageAccount);
    }
    if (eftDetails?.useSameBankAccount) {
      return isMemberPay ? i18n.t(tokens.labels.banking.legalAccessVerbiageBoth) : i18n.t(tokens.labels.banking.legalAccessVerbiageDeposit);
    }
    return formType === 'default' ? i18n.t(tokens.labels.banking.legalAccessVerbiageDeposit) : i18n.t(tokens.labels.banking.legalAccessVerbiageWithdraw);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, getHeadingText(), /*#__PURE__*/React.createElement("div", {
    style: styles.column
  }, /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.banking.financialInstitutionNumber),
    onChange: e => handleChange(formType, 'institutionNum', e.target.value, e),
    value: institutionNum,
    error: Boolean(institutionNumError),
    helperText: institutionNumError
  }), /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.banking.branchTransitNumber),
    onChange: e => handleChange(formType, 'transitNum', e.target.value, e),
    value: transitNum,
    error: Boolean(transitNumError),
    helperText: transitNumError
  })), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.banking.accountHolderName),
    value: accountHolder,
    onChange: e => handleChange(formType, 'accountHolder', e.target.value, e),
    error: Boolean(accountHolderError),
    helperText: accountHolderError || i18n.t(tokens.labels.banking.asItAppearsOnYourCheque),
    fullWidth: true,
    customHelperTextProps: {
      fontFamily: Theme.fonts.type.heading
    }
  }), /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.banking.accountNumber),
    value: accountNum,
    onChange: e => handleChange(formType, 'accountNum', e.target.value, e),
    error: Boolean(accountNumError),
    helperText: accountNumError || i18n.t(tokens.labels.banking.ifItStartsWithZeroInclude),
    fullWidth: true,
    customHelperTextProps: {
      fontFamily: Theme.fonts.type.heading
    }
  }), /*#__PURE__*/React.createElement(FormControl, {
    error: Boolean(acceptedError),
    ref: acceptedErrorRef
  }, /*#__PURE__*/React.createElement(FormGroup, {
    row: true,
    style: {
      marginTop: 20
    }
  }, acceptedError && /*#__PURE__*/React.createElement(FormHelperText, {
    style: {
      marginBottom: 10
    }
  }, acceptedError), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: accepted,
      onChange: () => handleChange(formType, 'accepted', !accepted, true),
      value: "acceptedTerms",
      color: "secondary"
    }),
    label: /*#__PURE__*/React.createElement(Text, {
      avMedium: true,
      customStyle: {
        paddingRight: "5px"
      }
    }, _getLegalAccessVerbiage())
  }))), isValidated && institutionAddress ? /*#__PURE__*/React.createElement("div", {
    ref: validatedMessageCardRef
  }, /*#__PURE__*/React.createElement(MessageCard, {
    title: institutionName,
    subtitle: institutionAddress
  })) : null, !isValidated && branchValidationError ? /*#__PURE__*/React.createElement("div", {
    ref: branchErrorCardRef
  }, /*#__PURE__*/React.createElement(MessageCard, {
    title: i18n.t(tokens.labels.banking.bankingValidationFailed),
    subtitle: i18n.t(tokens.labels.banking.providedBankCouldNotBeValidated),
    isError: true
  })) : null);
};
export default EftDetailsForm;